import { Component, Injector, OnInit } from '@angular/core';
import { UntypedFormArray, FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/Services/api.service';
import { OrganizationsService } from 'src/app/Services/organizations.service';
import { ModelEditor } from 'src/app/shared/editors/modeleditor';
import { EditlegacysigndisplayruleComponent } from '../editlegacysigndisplayrule/editlegacysigndisplayrule.component';
import { trigger, state, style, transition, group, animate, stagger, query, animateChild } from '@angular/animations';
import { findIndex } from 'rxjs';
import { AnimationEvent } from "@angular/animations";

@Component({
    selector: 'app-editsigndisplayruleset',
    templateUrl: './editsigndisplayruleset.component.html',
    styleUrls: ['./editsigndisplayruleset.component.scss'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ height: '0', opacity: '0' }),
                query('@*', animateChild(), { optional: true }),
                animate('.3s ease-out', style({ height: '3em' })),
                animate('.1s ease-out', style({ opacity: '1' }))
            ]),
            transition(':leave', [
                style({ height: '3em', opacity: '1' }),
                query('@*', animateChild(), { optional: true }),
                animate('.1s ease-out', style({ opacity: '0' })),
                animate('.3s ease-out', style({ height: '0' }))
            ])
        ]),
        trigger('move', [
            transition(':increment', [
                style({ transform: '*' }),
                group([
                    query('@*', animateChild(), { optional: true }),
                    animate('.3s ease-out', style({ transform: 'translateY(100%)' }))
                ])
            ]),
            transition(':decrement', [
                style({ transform: '*' }),
                group([
                    query('@*', animateChild(), { optional: true }),
                    animate('.3s ease-out', style({ transform: 'translateY(-100%)' }))
                ])
            ])
        ])
    ],
    standalone: false
})
export class EditsigndisplayrulesetComponent extends ModelEditor implements OnInit {

  public ModelTypes: any = [];
  public ModelProperties: any = [];
  public dialogVisible = true;
  DataTypeMap: Map<string, string> = new Map([
    ['88', 'fa fa-times'],
    ['89', 'fa fa-check'],
    ['160', 'fa fa-arrow-up'],
    ['161', 'fa fa-arrow-up-right'],
    ['162', 'fa fa-arrow-right'],
    ['163', 'fa fa-arrow-down-right'],
    ['164', 'fa fa-arrow-down'],
    ['165', 'fa fa-arrow-down-left'],
    ['166', 'fa fa-arrow-left'],
    ['167', 'fa fa-arrow-up-left'],
  ]);
  public animationStates: number[] = [];
  private isSwapping = false;

  constructor(private apiService: ApiService, public organizationsService: OrganizationsService, private route: ActivatedRoute, private toastService: ToastrService, private modalService: SimpleModalService, private injector: Injector, private fb: FormBuilder) {
    super("infrastructure/displayrulesets", injector);
    apiService.Get("common/modeltypes/parkingstructure").then(result => {
      this.ModelTypes = result;
    });
    this.Form?.get('TargetType')?.valueChanges.subscribe(value => {
      if (value) {
        this.onTargetTypeChange(value);
      }
    });
  }

  public onTargetTypeSelect(event: any) {
    var count = this.Model.SignConfigurationsCount;
    if (count > 0) {
      this.toastService.warning(`Changing the Target Type will affect ${count} sign configuration(s). The affected signs will have their target structures cleared.`);
    }
  }

  private onTargetTypeChange(targetType: string) {
    // Update model properties when target type changes
    this.apiService.Get("common/modeltypes/" + targetType + "/properties").then(result => {
      this.ModelProperties = result;
    });
  }

  public override DefaultModel() {
    return {Name: 'Sign Ruleset', TargetType: '', DisplayRules: []};
  }
  public override AfterModelLoaded(): void {
    // Initialize animation states after model is loaded
    this.updateAnimationStates();
  }
  public override BeforeSave(): boolean | void {
  }
  public override Validators() {
  }

  // Helper method to update animation states array
  private updateAnimationStates(): void {
    const rules = this.Form.get("DisplayRules") as UntypedFormArray;
    if (rules) {
      // Initialize with positions (0, 1, 2, etc.)
      this.animationStates = Array.from({ length: rules.length }, (_, i) => i);
    }
  }

  public AddDisplayRule() : void {
    const rulesArray = this.Form.get("DisplayRules") as UntypedFormArray;
    
    // Find the highest existing DisplayIndex
    let highestIndex = 0;
    rulesArray.controls.forEach(control => {
      const displayIndex = control.get('DisplayIndex')?.value || 0;
      highestIndex = Math.max(highestIndex, displayIndex);
    });

    // New index will be one more than the highest existing index
    const newIndex = highestIndex + 1;
    
    var newline = {
      Id: null, 
      ClassName: 'SignConfigurationLegacyTextDisplayRule', 
      Brightness: 100, 
      Mode: 1, 
      SplitMode: 0, 
      TextColor: 0, 
      TextAlignment: 0, 
      DataType: 0, 
      ShowStyle: 0, 
      SecondDisplayTextColor: 0, 
      SecondDisplayTextAlignment: 0, 
      SecondDisplayDataType: 0, 
      SecondDisplayShowStyle: 0, 
      Content: '', 
      SecondDisplayContent: '', 
      StayTimeInSeconds: 0, 
      Speed: 0, 
      SecondDisplayStayTimeInSeconds: 0, 
      SecondDisplaySpeed: 0, 
      DisplayIndex: newIndex,
      Index: newIndex, 
      SecondsBetweenToggling: 0,
      ConditionGroup: {
        Id: 0,
        RequireAllConditions: false,
        Conditions: [],
        ChildGroups: []
      }
    };
    
    this.AddToFormArray(rulesArray, newline, "DisplayRules");
    // Update animation states after adding a new rule
    this.updateAnimationStates();
  }

  public RemoveDisplayRule(index: any) : void {
    this.FormArray(this.Form, "DisplayRules").removeAt(index);
    this.RecalculateIndexes();
    this.Form.markAsDirty();
    // Update animation states after removing a rule
    this.updateAnimationStates();
  }

  public moveUp(index: number) : void {
    if (index > 0 && !this.isSwapping){
      // Increment/decrement the position values to trigger animations
      this.animationStates[index]--;
      this.animationStates[index-1]++;
      
      // Still update the Index property for data purposes
      var rules = this.Form.get("DisplayRules") as UntypedFormArray;
      var rule = rules.at(index);
      rule.get('Index')?.setValue(rule.get('Index')?.value - 1);
      var rule2 = rules.at(index - 1);
      rule2.get('Index')?.setValue(rule2.get('Index')?.value + 1);
    }
  }

  public TriggerSwap(index: number, event: AnimationEvent): void {
    if (event.fromState == 'void' || this.isSwapping){
      return;
    }

    //each swap will trigger this event twice, one for the row shifted up and one for the row shifted down.
    //only processes the shift down event - we only want to process once to prevent messing with the indexes.
    if (event.fromState > event.toState){
      this.isSwapping = true;
      
      var rules = this.Form.get("DisplayRules") as UntypedFormArray;
      
      // Store the original form controls
      const originalControls = rules.controls.slice();
      
      // Swap the actual form controls instead of just the values
      if (index > 0) {
        const temp = rules.at(index);
        rules.setControl(index, rules.at(index-1));
        rules.setControl(index-1, temp);
        
        // Update DisplayIndex values
        const control1 = rules.at(index);
        const control2 = rules.at(index-1);
        const displayIndex1 = control1.get('DisplayIndex')?.value;
        const displayIndex2 = control2.get('DisplayIndex')?.value;
        
        control1.get('DisplayIndex')?.setValue(displayIndex2);
        control2.get('DisplayIndex')?.setValue(displayIndex1);
        
        // Reset animation states to their default positions after swapping
        this.UseAnimation = false;
        // Reset animation states to match the new order
        this.updateAnimationStates();
      } else {
        // If index is 0 or invalid, restore original controls to prevent errors
        for (let i = 0; i < originalControls.length; i++) {
          rules.setControl(i, originalControls[i]);
        }
      }
      
      // Mark the form as dirty
      this.Form.markAsDirty();
      
      // Reset the swapping flag after a short delay
      setTimeout(() => {
        this.UseAnimation = true;
        this.isSwapping = false;
      }, 50);
    }
  }

  public moveDown(index: number) : void {
    var rules = (this.Form.get("DisplayRules") as UntypedFormArray);
    if (index < rules.value.length-1 && !this.isSwapping) {
      // Increment/decrement the position values to trigger animations
      this.animationStates[index]++;
      this.animationStates[index+1]--;
      
      // Still update the Index property for data purposes
      var rule = rules.at(index);
      rule.get('Index')?.setValue(rule.get('Index')?.value + 1);
      var rule2 = rules.at(index + 1);
      rule2.get('Index')?.setValue(rule2.get('Index')?.value - 1);
    }
  }

  public RecalculateIndexes(): void {
    for (let i = 0; i <(this.Form.get("DisplayRules") as UntypedFormArray).length; i++){
      var rule = (this.Form.get("DisplayRules") as UntypedFormArray).at(i);
      rule.get('Index')?.setValue(i+1);
      rule.get('DisplayIndex')?.setValue(i+1);
    }
  }

  public EditDisplayRule(index: any) : void {
    let displayRule = (this.Form.get('DisplayRules') as UntypedFormArray).at(index);
    switch(displayRule.get('ClassName')?.value)
    {
      case 'SignConfigurationLegacyTextDisplayRule':
      {
        this.modalService.addModal(EditlegacysigndisplayruleComponent, 
          { 
            Form: (this.Form.get('DisplayRules') as UntypedFormArray).at(index), 
            TargetType: this.Form.get('TargetType')?.value 
          }).subscribe((result) => {
        });
      }
    }
  }

  public GetContentIcon(datatype : string) : string|undefined {
    if (datatype == '1'|| datatype == '2')
      return '';
    return this.DataTypeMap.get(datatype.toString());
  }

  public getActiveDays(condition: any): string {
    const days = [];
    if (condition.ActiveMonday) days.push('Mon');
    if (condition.ActiveTuesday) days.push('Tue');
    if (condition.ActiveWednesday) days.push('Wed');
    if (condition.ActiveThursday) days.push('Thu');
    if (condition.ActiveFriday) days.push('Fri');
    if (condition.ActiveSaturday) days.push('Sat');
    if (condition.ActiveSunday) days.push('Sun');
    return days.join(', ');
  }
}
